import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { casesService } from "../services/cases.service";
import type {
  ClientIdentityFields,
  ClientIdentityPreview,
} from "../types/clientIdentity";
import { createLatestRequestGuard } from "../utils/latestRequestGuard";

/**
 * Matches the accessioning prefix lookup so the operator sees the identity consequence settle at
 * the same pace in both places.
 */
const PREVIEW_DEBOUNCE_MS = 600;

/**
 * Marks a response that arrived after a newer candidate had already been resolved. It is thrown
 * rather than returned so the void answer can never reach the panel, and it is filtered out of
 * the reported error — a discarded answer is not a failure the operator should see.
 */
class StaleClientIdentityPreviewError extends Error {
  constructor() {
    super("Client identity preview superseded by a newer candidate");
    this.name = "StaleClientIdentityPreviewError";
  }
}

export interface UseClientIdentityPreviewResult {
  preview: ClientIdentityPreview | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Resolves what the candidate client values would mean for the case's prefix, case number and
 * billing scenario, debounced while the operator is still choosing.
 *
 * Carries a generation guard mirroring `prefixLookupGuardRef` in `AccessioningPage`: the operator
 * decides whether to renumber based on what this panel says, so a slow answer for an abandoned
 * candidate must never overwrite the answer for the one now on screen.
 */
export function useClientIdentityPreview(
  caseId: string | undefined,
  candidate: ClientIdentityFields,
  enabled = true
): UseClientIdentityPreviewResult {
  const guardRef = useRef(createLatestRequestGuard());

  // Stable identity while the four values are unchanged, so a caller passing an object literal
  // does not restart the debounce on every render.
  const stableCandidate = useMemo<ClientIdentityFields>(
    () => ({
      sendingPracticeId: candidate.sendingPracticeId,
      physicianId: candidate.physicianId,
      surgeryCenterLabId: candidate.surgeryCenterLabId,
      submittingLabId: candidate.submittingLabId,
    }),
    [
      candidate.sendingPracticeId,
      candidate.physicianId,
      candidate.surgeryCenterLabId,
      candidate.submittingLabId,
    ]
  );

  const [debouncedCandidate] = useDebouncedValue(
    stableCandidate,
    PREVIEW_DEBOUNCE_MS
  );

  const isQueryEnabled = enabled && !!caseId;

  const fetchPreview = useCallback(async (): Promise<ClientIdentityPreview> => {
    const generation = guardRef.current.begin();
    const result = await casesService.previewClientIdentity(
      caseId!,
      debouncedCandidate
    );
    if (!guardRef.current.isCurrent(generation)) {
      throw new StaleClientIdentityPreviewError();
    }
    return result;
  }, [caseId, debouncedCandidate]);

  const query = useQuery({
    queryKey: ["client-identity-preview", caseId, debouncedCandidate],
    queryFn: fetchPreview,
    enabled: isQueryEnabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Discard any answer still in flight when the panel goes away, so it cannot be applied to a
  // candidate nobody is looking at any more.
  useEffect(() => {
    const guard = guardRef.current;
    return () => guard.invalidate();
  }, []);

  const isSettling = isQueryEnabled && debouncedCandidate !== stableCandidate;

  const error = useMemo<Error | null>(() => {
    if (!query.error) return null;
    return query.error instanceof StaleClientIdentityPreviewError
      ? null
      : query.error;
  }, [query.error]);

  return {
    preview: query.data ?? null,
    isLoading: isSettling || query.isFetching,
    error,
  };
}
